/**
 * DataSourcesPage.jsx — Phase 12 mandate #6
 *
 * Public transparency page (accessible by every logged-in user, not
 * gated to CEO) that lists every metric visible in the Customer Hub,
 * its classification tier, its exact source, formula, and last update.
 *
 * Purpose: build team trust by making it possible to click any number
 * in the Hub and read "how this was calculated" without asking anyone.
 *
 * Route: /data-sources
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { METRIC_CLASSIFICATION, TIER_META } from "./metric_classification";
import MetricBadge from "./MetricBadge";
import VerifyModal from "./VerifyModal";
import BonatUploadCard from "./BonatUploadCard";
import DemographicsPanel from "./DemographicsPanel";
import TotalBreakdownCard from "./TotalBreakdownCard";
import { InfoTip } from "../components/InfoTip";
import "./hub_responsive.css";
import { formatKSADate } from "../lib/ksaTime";

// Group by tier for the page layout
const TIER_ORDER = ["measured", "projected", "predicted"];

const CATEGORY_LABEL = {
  measured:  "المقاييس المؤكَّدة — تُعرَض للفريق",
  projected: "المُسقَطة — للـ CEO فقط",
  predicted: "المتوقَّعة — للـ CEO فقط",
};

const CATEGORY_INTRO = {
  measured:  "كل رقم في هذه القائمة يُقرَأ 100% من مصدر بيانات فعلي (pos_sales / product_costs / datasets). لا حسابات إسقاطية ولا نماذج توقّع. هذه الأرقام تُبنى عليها القرارات اليومية.",
  projected: "أرقام تعتمد على استقراء رياضي (مثل: الإنفاق السنوي = إنفاق 90 يوم × 365/90). لا تعكس الواقع الحاليّ إلا بافتراض ثبات المعدَّل. تُعرَض للـ CEO فقط، لأن الفريق يحتاج مقاييس مؤكَّدة.",
  predicted: "توقّعات إحصائية بهامش خطأ (نموذج v4 دقته 48.2% ± 5.4%). مفيدة للتخطيط، لكنها ليست حقائق. تُعرَض للـ CEO فقط.",
};

export default function DataSourcesPage() {
  const [auth, setAuth] = useState({ loading: true, user: null, error: null });
  const [health, setHealth] = useState({ loading: true, data: null });
  const [verifyMetric, setVerifyMetric] = useState(null);   // metric_id opened in modal

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setAuth({ loading: false, user: r.data, error: null }))
      .catch((e) => setAuth({
        loading: false, user: null,
        error: e?.response?.status === 401 ? "unauthenticated" : (e?.message || "خطأ"),
      }));
  }, []);

  // Best-effort data freshness read from segments distribution
  useEffect(() => {
    if (!auth.user) return;
    api.get("/segments/distribution", { params: { window_days: 90, paid_only: true } })
      .then((r) => setHealth({ loading: false, data: r.data }))
      .catch(() => setHealth({ loading: false, data: null }));
  }, [auth.user]);

  // ب‑2 (2026-10) — «العملاء الفريدون في النافذة» يأتي من الدالة المشتركة
  // core.pos_data.unique_customers_window (نفس التي تستخدمها data-readiness)
  // لا من segments/distribution (التي تضمّ «المفقود» التاريخي خارج النافذة).
  const [uniq, setUniq] = useState({ loading: true, data: null });
  useEffect(() => {
    if (!auth.user) return;
    api.get("/customers/unique-count", { params: { days: 90, paid_only: true } })
      .then((r) => setUniq({ loading: false, data: r.data }))
      .catch(() => setUniq({ loading: false, data: null }));
  }, [auth.user]);

  // v200.12 Phase 3 — coverage_from live state (per-metric window/mode
  // strip switches from 'تغطية غير مؤكدة' to the real oldest date whenever
  // the backend scheduler has written a fresh row).
  const [coverage, setCoverage] = useState({});
  useEffect(() => {
    if (!auth.user) return;
    api.get("/figure-contract/state")
      .then((r) => setCoverage(r.data?.state || {}))
      .catch(() => setCoverage({}));
  }, [auth.user]);


  const grouped = TIER_ORDER.reduce((acc, tier) => {
    acc[tier] = Object.entries(METRIC_CLASSIFICATION)
      .filter(([, v]) => v.tier === tier)
      .map(([id, v]) => ({ id, ...v }));
    return acc;
  }, {});

  const role = auth.user?.role;

  return (
    <div
      dir="rtl"
      data-testid="data-sources-page"
      className="ds-root"
      style={{
        minHeight: "100vh",
        background: "#F7F2EC",
        color: "#3D2A1C",
        fontFamily: "'El Messiri', -apple-system, 'Noto Naskh Arabic', sans-serif",
        fontVariantNumeric: "lining-nums tabular-nums",
        padding: "40px 24px",
      }}
    >
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <header style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <span style={{
              padding: "4px 10px", borderRadius: 999,
              background: "rgba(15,118,110,0.10)", color: "#0F766E",
              fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
              border: "1px solid rgba(15,118,110,0.35)",
            }}>
              شفافية · قابل للتحقق
            </span>
            <h1 className="ds-h1" style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: -0.6 }}>
              مصادر الأرقام في مركز العملاء
            </h1>
          </div>
          <p style={{ marginTop: 10, fontSize: 14, color: "#57534E", lineHeight: 1.7 }}>
            كل رقم تراه في مركز العملاء موثّق هنا: مصدره، طريقة حسابه،
            وتصنيفه (مقيس / مُسقَط / متوقَّع). إذا رأيت رقمًا في الشاشة
            بلا تصنيف — فهذا مخالف للدستور، أبلغ الفريق التقني فورًا.
          </p>
          {health.data && (
            <div data-testid="ds-freshness" className="ds-freshness" style={{
              marginTop: 14, padding: "10px 14px", borderRadius: 10,
              background: "#FFFFFF", border: "1px solid rgba(61,42,28,0.12)",
              display: "inline-flex", gap: 14, fontSize: 12, color: "#57534E",
              flexWrap: "wrap",
            }}>
              <span>🕒 آخر تحديث: <b>{health.data.ref_date || "—"}</b></span>
              <span>📊 نافذة: <b>{uniq.data?.window_days ?? health.data.window_days} يوم</b></span>
              <span data-testid="ds-unique-customers">👥 العملاء الفريدون في النافذة: <b>{uniq.data ? uniq.data.count?.toLocaleString("en-US") : "—"}</b>
                {uniq.data && <span style={{ fontSize: 10, color: "#78716C" }}> ({uniq.data.records_examined?.toLocaleString("en-US")} سجل مفحوص)</span>}
              </span>
              <span>🟢 وضع: <b>{(uniq.data?.mode ?? health.data.mode) === "paid_only" ? "المدفوعة فقط" : "كل الزيارات"}</b></span>
            </div>
          )}
        </header>

        {uniq.data && (
          <div data-testid="ds-unique-count-source" style={{
            marginTop: -8, marginBottom: 20, fontSize: 10.5, color: "#A8A29E",
          }}>
            مصدر «العملاء الفريدون»: core.pos_data.unique_customers_window · {uniq.data.since?.slice(0, 10)} → {uniq.data.until?.slice(0, 10)}
          </div>
        )}

        {!auth.loading && auth.user && <BonatUploadCard />}
        {!auth.loading && auth.user && <TotalBreakdownCard />}
        {!auth.loading && auth.user && <DemographicsPanel />}

        {auth.loading && (
          <div data-testid="ds-loading" style={{ padding: 40, textAlign: "center", color: "#78716C" }}>
            جارٍ التحقق من الصلاحية…
          </div>
        )}

        {!auth.loading && auth.error === "unauthenticated" && (
          <div data-testid="ds-unauth" style={{
            padding: 20, borderRadius: 14,
            background: "#FEF3F2", color: "#B91C1C",
            border: "1px solid rgba(185,28,28,0.35)",
          }}>
            يجب تسجيل الدخول أولًا لعرض هذه الصفحة.
          </div>
        )}

        {!auth.loading && auth.user && TIER_ORDER.map((tier) => {
          const meta = TIER_META[tier];
          const isCeoOnly = !meta.audienceAll;
          const canSee = !isCeoOnly || role === "ceo" || role === "admin";

          return (
            <section
              key={tier}
              data-testid={`ds-section-${tier}`}
              style={{ marginBottom: 32 }}
            >
              <div style={{
                display: "flex", alignItems: "center", gap: 12,
                marginBottom: 10, flexWrap: "wrap",
              }}>
                <MetricBadge metricId={grouped[tier][0]?.id} size="md" />
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#3D2A1C" }}>
                  {CATEGORY_LABEL[tier]}
                </h2>
                {isCeoOnly && (
                  <span style={{
                    fontSize: 10, padding: "3px 8px", borderRadius: 999,
                    background: "#FEF3C7", color: "#92400E", fontWeight: 700,
                  }}>
                    🔒 محجوب عن الفريق
                  </span>
                )}
              </div>
              <p style={{ fontSize: 13, color: "#57534E", marginBottom: 14, lineHeight: 1.7 }}>
                {CATEGORY_INTRO[tier]}
              </p>

              {!canSee ? (
                <div data-testid={`ds-blocked-${tier}`} style={{
                  padding: 16, borderRadius: 12,
                  background: "#FEF3C7", color: "#92400E",
                  border: "1px solid #F59E0B", fontSize: 13,
                }}>
                  🔒 محتوى هذا القسم مخصّص للـ CEO فقط. الفريق لا يحتاج
                  هذه المقاييس لاتخاذ قرارات يومية، والاطّلاع عليها قد
                  يُضلّل التوجّه.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {grouped[tier].map((m) => (
                    <article
                      key={m.id}
                      data-testid={`ds-metric-${m.id}`}
                      className="ds-metric-card"
                      style={{
                        padding: "14px 18px",
                        background: "#FFFFFF",
                        border: "1px solid rgba(61,42,28,0.12)",
                        borderRadius: 12,
                        display: "grid", gap: 6,
                      }}
                    >
                      <div className="ds-metric-row" style={{
                        display: "flex", justifyContent: "space-between",
                        alignItems: "center", gap: 8, flexWrap: "wrap",
                      }}>
                        <code style={{
                          fontSize: 13, fontWeight: 700, color: "#3D2A1C",
                          background: "rgba(170,149,102,0.14)",
                          padding: "3px 8px", borderRadius: 6, fontFamily: "monospace",
                        }}>{m.id}</code>
                        <MetricBadge metricId={m.id} size="xs" />
                      </div>
                      <div style={{ fontSize: 12, color: "#57534E", lineHeight: 1.7 }}>
                        {m.note}
                      </div>
                      {/* v200.12 Figure Contract Phase 2 — window / mode /
                          coverage_since strip. Rendered only if all five
                          mandatory fields are declared for this figure. */}
                      {m.computed_in === "backend" && m.window && m.mode && m.coverage_from && (() => {
                        const row = coverage[m.coverage_from];
                        let cov = "التغطية غير مؤكدة";
                        if (row && !row.stale && row.value) {
                          const d = new Date(row.value);
                          if (!isNaN(d.getTime())) {
                            cov = "التغطية منذ " + formatKSADate(d, { year: "numeric", month: "short", day: "numeric" });
                          }
                        }
                        return (
                          <div data-testid={`ds-figure-context-${m.id}`}
                               style={{ fontSize: 10, opacity: 0.7, color: "#4B5563" }}>
                            نافذة {m.window} · وضع {m.mode} · {cov}
                          </div>
                        );
                      })()}
                      {m.computed_in === "frontend" && (
                        <div data-testid={`ds-frontend-computed-${m.id}`}
                             style={{ fontSize: 10, opacity: 0.7, color: "#4B5563" }}>
                          محسوب في المتصفح · غير موثّق
                        </div>
                      )}
                      <div className="ds-metric-row" style={{
                        display: "flex", justifyContent: "space-between",
                        alignItems: "center", gap: 8, flexWrap: "wrap",
                      }}>
                        <div style={{ fontSize: 11, color: "#78716C", fontFamily: "monospace" }}>
                          📍 {m.source}
                        </div>
                        {tier === "measured" && (
                          <button
                            type="button"
                            data-testid={`ds-verify-${m.id}`}
                            onClick={() => setVerifyMetric(m.id)}
                            style={{
                              border: "1px solid rgba(15,118,110,0.35)",
                              cursor: "pointer",
                              padding: "5px 12px", borderRadius: 999,
                              fontSize: 11, fontWeight: 700,
                              background: "rgba(15,118,110,0.08)",
                              color: "#0F766E",
                              transition: "filter 150ms ease",
                            }}
 
                           aria-label="افتح 5 عملاء عشوائيين من الخام">
                            🔍 تحقّق بنفسك
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          );
        })}

        <footer style={{
          marginTop: 40, paddingTop: 20,
          borderTop: "1px solid rgba(61,42,28,0.12)",
          fontSize: 11, color: "#A8A29E", textAlign: "center",
        }}>
          Phase 12 · شفافية الأرقام · تُحدَّث تلقائيًا مع كل نشر جديد
        </footer>
      </div>

      {verifyMetric && (
        <VerifyModal
          metricId={verifyMetric}
          onClose={() => setVerifyMetric(null)}
        />
      )}
    </div>
  );
}
